import { SWRConfig } from 'swr';
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import Dynamic from '@base/Dynamic/Dynamic';
import LearnerProfile from '@cont/EducatorProfile/EducatorProfile';
import { useRouter } from 'next/router';
import { LEARNER_PROFILE } from '@constants/segment';
import Error from '@comp/Error/Error';
import {
  fetchLearnerProfileData,
  fetchUserProfileStats
} from '@api/fetchers/profile';
import useFullProfileInfo from '@api/hooks/profile/useFullProfileInfo';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import { fetchGoalInfo } from '@api/fetchers/goal';
import SEO from '@seo/index';
import Layout from '@layout/index';
import escape from '@utils/escapeHTML';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const LearnerCourses = Dynamic(() =>
  import('@cont/EducatorProfile/LearnerCourses')
);

const Learner = ({ statusCode, username, learnerUID, fallbackData }) => {
  const router = useRouter();
  const { keys, profileData: learner } = useFullProfileInfo({
    username,
    fallbackData
  });

  useEffect(() => {
    if (!learnerUID.startsWith('@')) {
      router.push('/learner/[learnerUID]', `/learner/@${username}`, {
        shallow: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (statusCode === 404) return <Error statusCode={404} />;

  const { firstName = '', lastName = '' } = learner;

  const title = `${firstName} ${lastName} (${username}) | Unacademy`;
  const url = `https://unacademy.com/learner/@${username}`;

  return (
    <SWRConfig
      value={{
        revalidateOnMount: false,
        fallback: {
          [keys.profileDataKey]: fallbackData.profileData,
          [keys.profileStatsKey]: fallbackData.profileStats
        }
      }}
    >
      <SEO title={title} description={null} url={url} />
      <LearnerProfile username={username}>
        <LearnerCourses username={username} />
      </LearnerProfile>
    </SWRConfig>
  );
};

Learner.pageInfo = {
  name: LEARNER_PROFILE,
  layoutOptions: {},
  isMWebSupported: true
};

export default Learner;

export const getStaticProps = async ({ params: query }) => {
  const { learnerUID } = query;
  let username = learnerUID;
  if (learnerUID.startsWith('@')) username = learnerUID.substr(1).toLowerCase();
  if (!username) return { notFound: true };

  username = escape(username);

  const promises = [];

  const profileData = await fetchLearnerProfileData(username);
  if (!profileData || profileData.errorCode)
    return { notFound: true, revalidate: 60 };

  const { isEducator, isExEducator } = profileData;
  const isEducatorProfile = isEducator || isExEducator;
  if (isEducatorProfile)
    return {
      redirect: {
        destination: `/@${encodeURI(username)}`,
        statusCode: 301
      }
    };

  const profileStats = await fetchUserProfileStats(username);

  const learner = profileData;
  const learnerGoalUid = learner?.leaderboardMetaInfo?.goal?.uid || null;
  if (learnerGoalUid)
    promises.push(
      resolvePromiseToObject(fetchGoalInfo(learnerGoalUid), 'learnerGoal')
    );
  if (promises.length) await Promise.all(promises);

  let fallbackData = {};
  if (promises.length) {
    fallbackData = await Promise.all(promises).then((resolvedVals) => {
      const resultObj = {};
      for (let i = 0; i < resolvedVals.length; i += 1)
        resultObj[resolvedVals[i].type] = resolvedVals[i].result;
      return resultObj;
    });
  }

  return {
    props: {
      isServer: true,
      username,
      learnerUID,
      fallbackData: {
        ...fallbackData,
        profileData,
        profileStats
      }
    },
    revalidate: 2 * 60
  };
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

Learner.propTypes = {
  statusCode: PropTypes.number.isRequired,
  username: PropTypes.string,
  learnerUID: PropTypes.string
};

Learner.defaultProps = {
  username: '',
  learnerUID: ''
};
Learner.Layout = Layout;
Learner.Wrapper = PageReduxProvider;
